// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * RenderersBuilder — manage renderer definitions owned by an agent.
 */
import { useCallback, useMemo, useState, type FC } from 'react'
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ConfirmDialog } from '@/components/forms'
import { useServices } from '@/app-shell/services'
import { useUpdateAgent, type Agent } from '@/subsystems/agents'
import { RendererEditDialog, type RendererEditData } from './RendererEditDialog'
import type { RendererDefinition } from '@/app-shell/types/workspace'

interface RenderersBuilderProps {
  agent: Agent
  workspaceId: string
  organizationId?: string
}

export const RenderersBuilder: FC<RenderersBuilderProps> = ({
  agent,
  workspaceId,
  organizationId,
}) => {
  const services = useServices()
  const apiClient = services.getClient()
  const orgId = organizationId ?? ''
  const updateAgent = useUpdateAgent(apiClient, orgId, workspaceId)

  const rendererList = useMemo(() => {
    return Object.entries(agent.renderers)
      .map(([k, r]) => ({ ...r, key: r.key || k }))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.label.localeCompare(b.label))
  }, [agent.renderers])

  const [editOpen, setEditOpen] = useState(false)
  const [editingRenderer, setEditingRenderer] = useState<RendererDefinition | null>(null)
  const [saving, setSaving] = useState(false)

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deletingKey, setDeletingKey] = useState<string | null>(null)

  const persistRenderers = useCallback(
    async (next: Record<string, RendererDefinition>) => {
      await updateAgent.mutateAsync({ agentId: agent.agentId, payload: { renderers: next } })
    },
    [updateAgent, agent.agentId],
  )

  const handleNew = () => {
    setEditingRenderer(null)
    setEditOpen(true)
  }

  const handleEdit = (renderer: RendererDefinition) => {
    setEditingRenderer(renderer)
    setEditOpen(true)
  }

  const handleSave = useCallback(async (data: RendererEditData) => {
    if (!orgId || !workspaceId) return
    setSaving(true)
    try {
      const next: Record<string, RendererDefinition> = { ...agent.renderers }
      next[data.key] = {
        key: data.key,
        label: data.label,
        component: data.component,
        slots: data.slots,
        columnType: data.columnType,
        order: data.order,
      }
      await persistRenderers(next)
      setEditOpen(false)
      toast.success(`Renderer "${data.label}" saved`)
    } catch {
      toast.error('Failed to save renderer')
    } finally {
      setSaving(false)
    }
  }, [orgId, workspaceId, agent.renderers, persistRenderers])

  const handleMove = useCallback(async (index: number, direction: -1 | 1) => {
    if (!orgId || !workspaceId) return
    const target = index + direction
    if (target < 0 || target >= rendererList.length) return
    const reordered = [...rendererList]
    const [moved] = reordered.splice(index, 1)
    reordered.splice(target, 0, moved)
    const next: Record<string, RendererDefinition> = {}
    reordered.forEach((r, i) => {
      next[r.key] = { ...r, order: i }
    })
    try {
      await persistRenderers(next)
    } catch {
      toast.error('Failed to reorder renderers')
    }
  }, [orgId, workspaceId, rendererList, persistRenderers])

  const handleDeleteConfirm = useCallback(async () => {
    if (!orgId || !workspaceId || !deletingKey) return
    try {
      const next = { ...agent.renderers }
      delete next[deletingKey]
      await persistRenderers(next)
      setDeleteConfirmOpen(false)
      toast.success('Renderer deleted')
    } catch {
      toast.error('Failed to delete renderer')
    }
  }, [orgId, workspaceId, deletingKey, agent.renderers, persistRenderers])

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="text-sm font-medium">Renderers</h3>
        <Button size="sm" onClick={handleNew}>
          <Plus className="h-4 w-4 mr-1" />
          New Renderer
        </Button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {rendererList.length === 0 ? (
          <div className="text-center text-muted-foreground py-10 text-sm">
            No renderers configured
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Order</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Component</TableHead>
                <TableHead>Slots</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rendererList.map((rnd, i) => (
                <TableRow key={rnd.key} data-testid={`renderer-row-${rnd.key}`}>
                  <TableCell>
                    <div className="flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={i === 0}
                        onClick={() => handleMove(i, -1)}
                        aria-label="Move up"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={i === rendererList.length - 1}
                        onClick={() => handleMove(i, 1)}
                        aria-label="Move down"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{rnd.key}</TableCell>
                  <TableCell>{rnd.label}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{rnd.component}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {rnd.slots.length > 0
                      ? rnd.slots.map(s => s.datasetKey ?? s.type).join(', ')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        aria-label={`Edit renderer ${rnd.key}`}
                        onClick={() => handleEdit(rnd)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        aria-label={`Delete renderer ${rnd.key}`}
                        onClick={() => {
                          setDeletingKey(rnd.key)
                          setDeleteConfirmOpen(true)
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <RendererEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        renderer={editingRenderer}
        onSave={handleSave}
        saving={saving}
        orgId={orgId}
        workspaceId={workspaceId}
        agentId={agent.agentId}
      />
      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Renderer"
        description={`Delete "${deletingKey}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
      />
    </div>
  )
}
